import {
	createFileRoute,
	Outlet,
	useRouterState,
} from "@tanstack/react-router";
import {
	ArrowRight,
	CalendarDays,
	CheckCircle2,
	Clock3,
	Download,
	Loader2,
	Mail,
	Share2,
	User,
} from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { HeroBeam } from "@/components/hero-beam";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { Reveal } from "@/components/reveal";
import { PlatformIcon } from "@/components/social-platform-logos";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";
import {
	WEBINAR_DATE_LABEL,
	WEBINAR_DURATION_LABEL,
	WEBINAR_END_ISO,
	WEBINAR_FORM_DATE_LABEL,
	WEBINAR_PUBLIC_URL,
	WEBINAR_ROOM_URL,
	WEBINAR_START_ISO,
	WEBINAR_TIME_LABEL,
	WEBINAR_TITLE,
} from "@/lib/webinar-constants";

const description =
	"Join AgentReacher's free webinar on July 1, 2026 at 19:30 CEST and learn how AI agents can execute marketing by connecting ChatGPT and Claude directly to social accounts.";

const calendarInviteHref = `data:text/calendar;charset=utf-8,${encodeURIComponent(
	[
		"BEGIN:VCALENDAR",
		"VERSION:2.0",
		"PRODID:-//AgentReacher//Webinar//EN",
		"BEGIN:VEVENT",
		"UID:agentreacher-webinar-20260701@agentreacher.com",
		"DTSTAMP:20260520T000000Z",
		"DTSTART:20260701T173000Z",
		"DTEND:20260701T183000Z",
		`SUMMARY:${WEBINAR_TITLE}`,
		"DESCRIPTION:Free AgentReacher webinar: AI agents as your marketing execution layer.",
		`LOCATION:${WEBINAR_ROOM_URL}`,
		`URL:${WEBINAR_ROOM_URL}`,
		"END:VEVENT",
		"END:VCALENDAR",
	].join("\r\n"),
)}`;

const agendaItems = [
	{
		title: "Find the manual work hiding in your content workflow",
		description:
			"Map the copy-pasting, rewriting, scheduling, and publishing steps that still slow down AI-assisted marketing.",
	},
	{
		title: "Connect ChatGPT or Claude to your social accounts",
		description:
			"Turn your AI assistant from a writing tool into the operator between ideas, approvals, and publishing.",
	},
	{
		title: "Live demo: prompt to scheduled posts",
		description:
			"Watch one brief become platform-specific posts that are drafted, adapted, and placed on the calendar.",
	},
	{
		title: "Build repeatable workflows for launches and outreach",
		description:
			"Use reusable inputs, approvals, and account rules so personalized publishing does not depend on manual busywork.",
	},
	{
		title: "Q&A",
		description:
			"Ask how to adapt the workflow to your current stack, audience, approval process, and inbound goals.",
	},
];

const platforms = [
	{
		name: "Instagram",
		bg: "bg-[radial-gradient(circle_at_30%_107%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285AEB_90%)]",
	},
	{ name: "Facebook", bg: "bg-[#1877F2]" },
	{ name: "LinkedIn", bg: "bg-[#0A66C2]" },
	{ name: "X", bg: "bg-black" },
	{ name: "TikTok", bg: "bg-black" },
	{ name: "YouTube", bg: "bg-[#FF0000]" },
	{ name: "Pinterest", bg: "bg-[#BD081C]" },
	{ name: "Threads", bg: "bg-black" },
	{ name: "Bluesky", bg: "bg-[#0085FF]" },
];

const eventSchema = {
	"@context": "https://schema.org",
	"@type": "Event",
	name: `Free webinar: ${WEBINAR_TITLE}`,
	description,
	eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
	eventStatus: "https://schema.org/EventScheduled",
	startDate: WEBINAR_START_ISO,
	endDate: WEBINAR_END_ISO,
	location: {
		"@type": "VirtualLocation",
		url: WEBINAR_ROOM_URL,
	},
	organizer: {
		"@type": "Organization",
		name: "AgentReacher",
		url: "https://agentreacher.com",
	},
};

export const Route = createFileRoute("/webinar")({
	component: WebinarPage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/webinar" });

		return {
			links: buildLocalizedSeoLinks({ matches, internalPath: "/webinar" }),
			meta: [
				{ title: `Free Webinar: ${WEBINAR_TITLE}` },
				{ name: "description", content: description },
				{ property: "og:title", content: `Free Webinar: ${WEBINAR_TITLE}` },
				{ property: "og:description", content: description },
				{ property: "og:image", content: ogImageUrl("webinar") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("webinar") },
			],
		};
	},
});

function WebinarPage() {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});

	if (pathname === "/webinar/live") {
		return <Outlet />;
	}

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<script
				type="application/ld+json"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: Static JSON-LD for public event metadata.
				dangerouslySetInnerHTML={{ __html: JSON.stringify(eventSchema) }}
			/>

			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						<section className="relative overflow-hidden rounded-md bg-white dark:bg-black">
							<div className="grid gap-px bg-border lg:grid-cols-3">
								<div className="homepage-hover-cell flex min-h-[34rem] flex-col justify-start rounded-md bg-white px-5 py-10 sm:px-10 lg:col-span-2 lg:px-12 dark:bg-black">
									<Reveal>
										<div className="flex flex-col items-start gap-6">
											<div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-foreground shadow-sm">
												<CalendarDays className="size-3.5 shrink-0 text-muted-foreground" />
												{WEBINAR_FORM_DATE_LABEL}
											</div>
											<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
												Free live webinar
											</p>
										</div>
										<h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-5xl lg:text-6xl">
											Webinar: Boost Your Socials
										</h1>
										<p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
											A live session for founders, marketers, and creators who
											want ChatGPT or Claude to draft, adapt, schedule, and
											publish social content automatically.
										</p>
										<HeroPlatformStrip />
										<HeroActionButtons />
									</Reveal>
								</div>
								<div className="homepage-hover-cell rounded-md bg-white p-5 sm:p-8 lg:p-10 dark:bg-black">
									<Reveal delay={120}>
										<WebinarSignupForm />
									</Reveal>
								</div>
							</div>
						</section>

						<section className="grid gap-px bg-border md:grid-cols-3">
							<EventFact
								icon={CalendarDays}
								label="Date"
								value={WEBINAR_DATE_LABEL}
							/>
							<EventFact
								icon={Clock3}
								label="Time"
								value={WEBINAR_TIME_LABEL}
							/>
							<EventFact
								icon={CheckCircle2}
								label="Duration"
								value={WEBINAR_DURATION_LABEL}
							/>
						</section>

						<section className="grid gap-px bg-border lg:grid-cols-3">
							<div className="homepage-hover-cell rounded-md bg-white px-5 py-10 sm:px-10 lg:col-span-2 dark:bg-black">
								<Reveal>
									<div className="max-w-2xl">
										<h2 className="text-2xl font-semibold tracking-[-0.03em]">
											Agenda
										</h2>
										<p className="mt-3 text-sm leading-6 text-muted-foreground">
											Join live, get the setup path, and leave with a workflow
											you can apply to launches, product updates, and recurring
											social distribution.
										</p>

										<div className="mt-7 space-y-4">
											{agendaItems.map((item, index) => (
												<div
													className="grid gap-2 sm:grid-cols-[2rem_1fr] sm:gap-4"
													key={item.title}
												>
													<p className="text-sm leading-6 text-muted-foreground">
														{String(index + 1).padStart(2, "0")}
													</p>
													<div>
														<h3 className="text-sm font-semibold leading-6">
															{item.title}
														</h3>
														<p className="mt-1 text-sm leading-6 text-muted-foreground">
															{item.description}
														</p>
													</div>
												</div>
											))}
										</div>
									</div>
								</Reveal>
							</div>
							<div
								className="homepage-hover-cell flex min-h-[18rem] items-center justify-center rounded-md bg-white p-6 dark:bg-black"
								style={{
									backgroundImage:
										"radial-gradient(rgba(17,24,39,0.055) 1px, transparent 1px), radial-gradient(ellipse 90% 80% at 50% 50%, rgba(17,24,39,0.08) 0%, rgba(17,24,39,0.035) 38%, transparent 72%)",
									backgroundSize: "20px 20px, 100% 100%",
								}}
							>
								<Reveal delay={120}>
									<AgendaVisual />
								</Reveal>
							</div>
						</section>
					</div>
				</div>
			</main>

			<PublicFooter />
		</div>
	);
}

function HeroPlatformStrip() {
	return (
		<div className="mt-8 flex w-full flex-col items-start gap-4">
			<p className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
				Post to all your platforms
			</p>
			<div className="grid grid-cols-9 items-center justify-items-center gap-2 sm:flex sm:flex-wrap sm:gap-3">
				{platforms.map(({ name, bg }) => (
					<div
						key={name}
						title={name}
						className={`flex size-8 items-center justify-center rounded-full text-white transition-transform hover:-translate-y-0.5 sm:size-9 ${bg}`}
					>
						<PlatformIcon platform={name} className="size-4 sm:size-[18px]" />
					</div>
				))}
			</div>
		</div>
	);
}

function AgendaVisual() {
	return (
		<div className="flex items-center justify-center text-foreground/10">
			<HeroBeam className="scale-125" />
		</div>
	);
}

function EventFact({
	icon: Icon,
	label,
	value,
}: {
	icon: typeof CalendarDays;
	label: string;
	value: string;
}) {
	return (
		<div className="homepage-hover-cell rounded-md bg-white p-5 dark:bg-black">
			<Reveal>
				<div className="flex items-center gap-3">
					<span className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40">
						<Icon className="size-5 text-foreground" />
					</span>
					<div className="min-w-0">
						<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
							{label}
						</p>
						<p className="text-sm font-semibold leading-snug">{value}</p>
					</div>
				</div>
			</Reveal>
		</div>
	);
}

function HeroActionButtons() {
	return (
		<div className="mt-6 flex flex-col items-start gap-2 sm:flex-row sm:items-center">
			<ShareWebinarButton />
			<a
				className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted/50"
				download="agentreacher-webinar.ics"
				href={calendarInviteHref}
			>
				<Download className="size-4" />
				Calendar invite
			</a>
		</div>
	);
}

function ShareWebinarButton() {
	const [shareStatus, setShareStatus] = useState<"idle" | "copied">("idle");

	async function onShare() {
		const shareUrl =
			typeof window === "undefined" ? WEBINAR_PUBLIC_URL : window.location.href;
		const shareData = {
			title: WEBINAR_TITLE,
			text: "Free AgentReacher webinar: AI agents as your marketing execution layer.",
			url: shareUrl,
		};

		try {
			if (navigator.share) {
				await navigator.share(shareData);
				return;
			}

			await navigator.clipboard.writeText(shareUrl);
			setShareStatus("copied");
			window.setTimeout(() => setShareStatus("idle"), 1800);
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError") {
				return;
			}

			await navigator.clipboard.writeText(shareUrl);
			setShareStatus("copied");
			window.setTimeout(() => setShareStatus("idle"), 1800);
		}
	}

	return (
		<button
			className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted/50"
			onClick={onShare}
			type="button"
		>
			<Share2 className="size-4" />
			{shareStatus === "copied" ? "Link copied" : "Share webinar"}
		</button>
	);
}

function WebinarSignupForm() {
	const firstNameId = useId();
	const emailId = useId();
	const websiteId = useId();
	const newsletterOptInId = useId();
	const [firstName, setFirstName] = useState("");
	const [email, setEmail] = useState("");
	const [newsletterOptIn, setNewsletterOptIn] = useState(false);
	const [website, setWebsite] = useState("");
	const [status, setStatus] = useState<
		"idle" | "loading" | "success" | "error"
	>("idle");
	const [message, setMessage] = useState("");

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setStatus("loading");
		setMessage("");

		try {
			const response = await fetch("/api/webinar/signup", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					firstName,
					email,
					newsletterOptIn,
					website,
				}),
			});
			const payload = (await response.json().catch(() => null)) as {
				error?: string;
			} | null;

			if (!response.ok) {
				setStatus("error");
				setMessage(payload?.error || "Registration failed. Please try again.");
				return;
			}

			setFirstName("");
			setEmail("");
			setNewsletterOptIn(false);
			setWebsite("");
			setStatus("success");
			setMessage(
				"Your seat is confirmed. Check your email for the webinar link.",
			);
		} catch {
			setStatus("error");
			setMessage("Registration failed. Check your connection and try again.");
		}
	}

	return (
		<form className="h-full rounded-md p-0" onSubmit={onSubmit}>
			<div>
				<p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
					Reserve your seat
				</p>
				<h2 className="mt-2 text-2xl font-semibold tracking-[-0.03em]">
					Join live
				</h2>
			</div>

			{message && (
				<div
					className={
						status === "success"
							? "mt-5 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2.5 text-sm text-emerald-700 dark:text-emerald-300"
							: "mt-5 rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2.5 text-sm text-red-700 dark:text-red-300"
					}
				>
					{message}
				</div>
			)}

			<div className="mt-5 grid gap-4">
				<label className="grid gap-2 text-sm font-medium" htmlFor={firstNameId}>
					First name
					<div className="relative">
						<User className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<input
							id={firstNameId}
							autoComplete="given-name"
							className="h-11 w-full rounded-md border border-black/10 bg-[#f7f6f2] py-2.5 pl-10 pr-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:bg-white dark:border-white/15 dark:bg-white/5 dark:focus:bg-black"
							onChange={(event) => setFirstName(event.target.value)}
							placeholder="Ada"
							required
							value={firstName}
						/>
					</div>
				</label>

				<label className="grid gap-2 text-sm font-medium" htmlFor={emailId}>
					Email
					<div className="relative">
						<Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<input
							id={emailId}
							autoComplete="email"
							className="h-11 w-full rounded-md border border-black/10 bg-[#f7f6f2] py-2.5 pl-10 pr-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:bg-white dark:border-white/15 dark:bg-white/5 dark:focus:bg-black"
							onChange={(event) => setEmail(event.target.value)}
							placeholder="you@company.com"
							required
							type="email"
							value={email}
						/>
					</div>
				</label>

				<label className="hidden" htmlFor={websiteId}>
					Website
					<input
						id={websiteId}
						autoComplete="off"
						onChange={(event) => setWebsite(event.target.value)}
						tabIndex={-1}
						value={website}
					/>
				</label>

				<label
					className="flex cursor-pointer items-start gap-3 rounded-md border border-black/10 bg-[#f7f6f2] p-3 text-sm leading-6 text-[#4b5563] dark:border-white/15 dark:bg-white/5 dark:text-white/65"
					htmlFor={newsletterOptInId}
				>
					<input
						id={newsletterOptInId}
						checked={newsletterOptIn}
						className="mt-1 size-4 accent-primary"
						onChange={(event) => setNewsletterOptIn(event.target.checked)}
						type="checkbox"
					/>
					<span>
						Send me practical notes about AI agents, social publishing, and
						marketing workflows.
					</span>
				</label>
			</div>

			<button
				className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
				disabled={status === "loading"}
				type="submit"
			>
				{status === "loading" ? "Registering..." : "Confirm registration"}
				{status === "loading" ? (
					<Loader2 className="size-4 animate-spin" />
				) : (
					<ArrowRight className="size-4" />
				)}
			</button>

			<p className="mt-3 text-center text-xs leading-5 text-[#6b7280] dark:text-white/45">
				You will receive the join link and calendar invite by email.
			</p>
		</form>
	);
}
